// src/services/maintenanceService.js
// Maintenance & Preventive Maintenance Module — see docs/MAINTENANCE_MODULE.md.
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// Locations
const getLocations = () => axios.get(`${API_URL}/api/maintenance/locations`, auth()).then(r => r.data);
const createLocation = (data) => axios.post(`${API_URL}/api/maintenance/locations`, data, auth()).then(r => r.data);
const updateLocation = (id, data) => axios.put(`${API_URL}/api/maintenance/locations/${id}`, data, auth()).then(r => r.data);
const deleteLocation = (id) => axios.delete(`${API_URL}/api/maintenance/locations/${id}`, auth()).then(r => r.data);

// Vendors
const getVendors = (params = {}) => axios.get(`${API_URL}/api/maintenance/vendors`, { ...auth(), params }).then(r => r.data);
const createVendor = (data) => axios.post(`${API_URL}/api/maintenance/vendors`, data, auth()).then(r => r.data);
const updateVendor = (id, data) => axios.put(`${API_URL}/api/maintenance/vendors/${id}`, data, auth()).then(r => r.data);
const deleteVendor = (id) => axios.delete(`${API_URL}/api/maintenance/vendors/${id}`, auth()).then(r => r.data);
const importSqfContractor = (sqfContractorId) => axios.post(`${API_URL}/api/maintenance/vendors/import-sqf-contractor/${sqfContractorId}`, {}, auth()).then(r => r.data);
const addVendorToSqfHub = (vendorId) => axios.post(`${API_URL}/api/maintenance/vendors/${vendorId}/add-to-sqf-hub`, {}, auth()).then(r => r.data);

// Assets
const getAssets = (params = {}) => axios.get(`${API_URL}/api/maintenance/assets`, { ...auth(), params }).then(r => r.data);
const getAsset = (id) => axios.get(`${API_URL}/api/maintenance/assets/${id}`, auth()).then(r => r.data);
const createAsset = (data) => axios.post(`${API_URL}/api/maintenance/assets`, data, auth()).then(r => r.data);
const updateAsset = (id, data) => axios.put(`${API_URL}/api/maintenance/assets/${id}`, data, auth()).then(r => r.data);
const deleteAsset = (id) => axios.delete(`${API_URL}/api/maintenance/assets/${id}`, auth()).then(r => r.data);
const getAssetPhotoUploadUrl = (id, contentType) => axios.post(`${API_URL}/api/maintenance/assets/${id}/photo-upload-url`, { contentType }, auth()).then(r => r.data);
const getAssetQr = (id) => axios.get(`${API_URL}/api/maintenance/assets/${id}/qr`, auth()).then(r => r.data);
const getAssetQrLabelUrl = (id) => `${API_URL}/api/maintenance/assets/${id}/qr-label`;
const downloadAssetQrLabel = (id) => axios.get(`${API_URL}/api/maintenance/assets/${id}/qr-label`, { ...auth(), responseType: 'blob' }).then(r => r.data);
const downloadBatchQrLabels = (assetIds) => axios.post(`${API_URL}/api/maintenance/assets/batch-qr-labels`, { assetIds }, { ...auth(), responseType: 'blob' }).then(r => r.data);
const getFoodSafetyAssets = () => axios.get(`${API_URL}/api/maintenance/assets/food-safety-list`, auth()).then(r => r.data);
const linkAssetDocument = (assetId, data) => axios.post(`${API_URL}/api/maintenance/assets/${assetId}/documents`, data, auth()).then(r => r.data);
const unlinkAssetDocument = (assetId, linkId) => axios.delete(`${API_URL}/api/maintenance/assets/${assetId}/documents/${linkId}`, auth()).then(r => r.data);
const linkAssetPart = (assetId, data) => axios.post(`${API_URL}/api/maintenance/assets/${assetId}/parts`, data, auth()).then(r => r.data);
const unlinkAssetPart = (assetId, partId) => axios.delete(`${API_URL}/api/maintenance/assets/${assetId}/parts/${partId}`, auth()).then(r => r.data);
const scanQrCode = (qrCodeKey) => axios.get(`${API_URL}/api/maintenance/scan/${qrCodeKey}`, auth()).then(r => r.data);
const getAssetHealthChecks = (assetId) => axios.get(`${API_URL}/api/maintenance/assets/${assetId}/health-checks`, auth()).then(r => r.data);
const generateHealthWorkOrder = (assetId) => axios.post(`${API_URL}/api/maintenance/assets/${assetId}/generate-health-wo`, {}, auth()).then(r => r.data);

// Meters + readings
const getAssetMeters = (assetId) => axios.get(`${API_URL}/api/maintenance/assets/${assetId}/meters`, auth()).then(r => r.data);
const createMeter = (data) => axios.post(`${API_URL}/api/maintenance/meters`, data, auth()).then(r => r.data);
const updateMeter = (id, data) => axios.put(`${API_URL}/api/maintenance/meters/${id}`, data, auth()).then(r => r.data);
const deleteMeter = (id) => axios.delete(`${API_URL}/api/maintenance/meters/${id}`, auth()).then(r => r.data);
const getMeterReadings = (id) => axios.get(`${API_URL}/api/maintenance/meters/${id}/readings`, auth()).then(r => r.data);
const addMeterReading = (id, data) => axios.post(`${API_URL}/api/maintenance/meters/${id}/readings`, data, auth()).then(r => r.data);

// Parts
const getParts = (params = {}) => axios.get(`${API_URL}/api/maintenance/parts`, { ...auth(), params }).then(r => r.data);
const getPart = (id) => axios.get(`${API_URL}/api/maintenance/parts/${id}`, auth()).then(r => r.data);
const createPart = (data) => axios.post(`${API_URL}/api/maintenance/parts`, data, auth()).then(r => r.data);
const updatePart = (id, data) => axios.put(`${API_URL}/api/maintenance/parts/${id}`, data, auth()).then(r => r.data);
const deletePart = (id) => axios.delete(`${API_URL}/api/maintenance/parts/${id}`, auth()).then(r => r.data);
const getPartStockEvents = (id) => axios.get(`${API_URL}/api/maintenance/parts/${id}/stock-events`, auth()).then(r => r.data);
const addPartStockEvent = (id, data) => axios.post(`${API_URL}/api/maintenance/parts/${id}/stock-events`, data, auth()).then(r => r.data);

// PM Templates
const getPmTemplates = () => axios.get(`${API_URL}/api/maintenance/pm-templates`, auth()).then(r => r.data);
const getPmTemplate = (id) => axios.get(`${API_URL}/api/maintenance/pm-templates/${id}`, auth()).then(r => r.data);
const getPmCalendar = (params = {}) => axios.get(`${API_URL}/api/maintenance/pm-templates/calendar`, { ...auth(), params }).then(r => r.data);
const createPmTemplate = (data) => axios.post(`${API_URL}/api/maintenance/pm-templates`, data, auth()).then(r => r.data);
const updatePmTemplate = (id, data) => axios.put(`${API_URL}/api/maintenance/pm-templates/${id}`, data, auth()).then(r => r.data);
const deletePmTemplate = (id) => axios.delete(`${API_URL}/api/maintenance/pm-templates/${id}`, auth()).then(r => r.data);
const linkPmTemplatePart = (id, data) => axios.post(`${API_URL}/api/maintenance/pm-templates/${id}/parts`, data, auth()).then(r => r.data);
const unlinkPmTemplatePart = (id, partId) => axios.delete(`${API_URL}/api/maintenance/pm-templates/${id}/parts/${partId}`, auth()).then(r => r.data);
const generatePmNow = (id) => axios.post(`${API_URL}/api/maintenance/pm-templates/${id}/generate-now`, {}, auth()).then(r => r.data);

// Work Orders
const getWorkOrders = (params = {}) => axios.get(`${API_URL}/api/maintenance/work-orders`, { ...auth(), params }).then(r => r.data);
const getWorkOrder = (id) => axios.get(`${API_URL}/api/maintenance/work-orders/${id}`, auth()).then(r => r.data);
const createWorkOrder = (data) => axios.post(`${API_URL}/api/maintenance/work-orders`, data, auth()).then(r => r.data);
const updateWorkOrder = (id, data) => axios.put(`${API_URL}/api/maintenance/work-orders/${id}`, data, auth()).then(r => r.data);
const startWorkOrder = (id) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/start`, {}, auth()).then(r => r.data);
const holdWorkOrder = (id, onHoldReason) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/hold`, { onHoldReason }, auth()).then(r => r.data);
const resumeWorkOrder = (id) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/resume`, {}, auth()).then(r => r.data);
const cancelWorkOrder = (id, cancelReason) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/cancel`, { cancelReason }, auth()).then(r => r.data);
const deleteWorkOrder = (id) => axios.delete(`${API_URL}/api/maintenance/work-orders/${id}`, auth()).then(r => r.data);
const completeWorkOrder = (id, data) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/complete`, data, auth()).then(r => r.data);
const getCalibrationCert = (id) => axios.get(`${API_URL}/api/maintenance/work-orders/${id}/calibration-cert`, auth()).then(r => r.data);
const getWoComments = (id) => axios.get(`${API_URL}/api/maintenance/work-orders/${id}/comments`, auth()).then(r => r.data);
const addWoComment = (id, comment) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/comments`, { comment }, auth()).then(r => r.data);
const getWoPhotoUploadUrl = (id, contentType) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/photos/upload-url`, { contentType }, auth()).then(r => r.data);
const addWoPhoto = (id, data) => axios.post(`${API_URL}/api/maintenance/work-orders/${id}/photos`, data, auth()).then(r => r.data);
const deleteWoPhoto = (woId, photoId) => axios.delete(`${API_URL}/api/maintenance/work-orders/${woId}/photos/${photoId}`, auth()).then(r => r.data);

// Equipment Manual (AI Addendum)
const getManualUploadUrl = (assetId, contentType, fileSize) =>
  axios.post(`${API_URL}/api/maintenance/assets/${assetId}/manual/upload-url`, { contentType, fileSize }, auth()).then(r => r.data);
const saveManual = (assetId, data) =>
  axios.post(`${API_URL}/api/maintenance/assets/${assetId}/manual`, data, auth()).then(r => r.data);
const deleteManual = (assetId) =>
  axios.delete(`${API_URL}/api/maintenance/assets/${assetId}/manual`, auth()).then(r => r.data);
const getManualDownloadUrl = (assetId) =>
  axios.get(`${API_URL}/api/maintenance/assets/${assetId}/manual/download`, auth()).then(r => r.data);

// AI PM generation from manual — SSE so a long generation over a large manual isn't killed by
// Heroku's router (drops any request that hasn't sent a byte in 30s); mirrors
// services/foodSafetyService.js's sseStream() helper.
function sseStream(url, body, onProgress) {
  return new Promise(async (resolve, reject) => {
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify(body),
      });
    } catch (e) { return reject(e); }
    if (!response.ok) {
      const err = await response.json().catch(() => ({ message: 'Request failed' }));
      return reject(err);
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, '').trim();
          if (!line || line.startsWith(':')) continue;
          try {
            const ev = JSON.parse(line);
            if (ev.type === 'progress') onProgress?.(ev.message);
            else if (ev.type === 'done')  return resolve(ev);
            else if (ev.type === 'error') return reject(new Error(ev.message));
          } catch {}
        }
      }
    } catch (e) { reject(e); }
  });
}

const previewPmGeneration = (assetId, onProgress) =>
  sseStream(`${API_URL}/api/maintenance/assets/${assetId}/generate-pm`, {}, onProgress);
const createPmTemplatesFromSchedules = (assetId, schedules, logId) =>
  axios.post(`${API_URL}/api/maintenance/assets/${assetId}/generate-pm/create`, { schedules, logId }, auth()).then(r => r.data);
const getPmGenerationLogs = (assetId) =>
  axios.get(`${API_URL}/api/maintenance/assets/${assetId}/generate-pm/logs`, auth()).then(r => r.data);

// Machine Knowledge Assistant chat — streamed (same sseStream() helper as PM generation above;
// a manual-grounded chat call can also run well past Heroku's 30s router ceiling)
const sendChatMessage = (assetId, question, sessionId) =>
  sseStream(`${API_URL}/api/maintenance/assets/${assetId}/chat`, { question, sessionId });
const getChatHistory = (assetId, sessionId) =>
  axios.get(`${API_URL}/api/maintenance/assets/${assetId}/chat/history`, { ...auth(), params: { sessionId } }).then(r => r.data);
const getChatSessions = (assetId) =>
  axios.get(`${API_URL}/api/maintenance/assets/${assetId}/chat/sessions`, auth()).then(r => r.data);
const getSuggestedQuestions = (assetId) =>
  axios.get(`${API_URL}/api/maintenance/assets/${assetId}/chat/suggested-questions`, auth()).then(r => r.data);

// Reports
const getSummaryReport = () => axios.get(`${API_URL}/api/maintenance/reports/summary`, auth()).then(r => r.data);
const getCostReport = (params = {}) => axios.get(`${API_URL}/api/maintenance/reports/cost`, { ...auth(), params }).then(r => r.data);
const getDowntimeReport = (params = {}) => axios.get(`${API_URL}/api/maintenance/reports/downtime`, { ...auth(), params }).then(r => r.data);
const getEquipmentOverview = () => axios.get(`${API_URL}/api/maintenance/reports/equipment-overview`, auth()).then(r => r.data);

// Direct-to-S3 upload helper (shared two-step pattern used across the app: get a presigned PUT
// URL, then PUT the file straight to S3).
const uploadToPresignedUrl = (uploadUrl, file) =>
  axios.put(uploadUrl, file, { headers: { 'Content-Type': file.type } });

const maintenanceService = {
  getLocations, createLocation, updateLocation, deleteLocation,
  getVendors, createVendor, updateVendor, deleteVendor, importSqfContractor, addVendorToSqfHub,
  getAssets, getAsset, createAsset, updateAsset, deleteAsset,
  getAssetPhotoUploadUrl, getAssetQr, getAssetQrLabelUrl, downloadAssetQrLabel, downloadBatchQrLabels,
  getFoodSafetyAssets, linkAssetDocument, unlinkAssetDocument, linkAssetPart, unlinkAssetPart, scanQrCode,
  getAssetHealthChecks, generateHealthWorkOrder,
  getManualUploadUrl, saveManual, deleteManual, getManualDownloadUrl,
  previewPmGeneration, createPmTemplatesFromSchedules, getPmGenerationLogs,
  sendChatMessage, getChatHistory, getChatSessions, getSuggestedQuestions,
  getAssetMeters, createMeter, updateMeter, deleteMeter, getMeterReadings, addMeterReading,
  getParts, getPart, createPart, updatePart, deletePart, getPartStockEvents, addPartStockEvent,
  getPmTemplates, getPmTemplate, getPmCalendar, createPmTemplate, updatePmTemplate, deletePmTemplate,
  linkPmTemplatePart, unlinkPmTemplatePart, generatePmNow,
  getWorkOrders, getWorkOrder, createWorkOrder, updateWorkOrder, startWorkOrder, holdWorkOrder,
  resumeWorkOrder, cancelWorkOrder, deleteWorkOrder, completeWorkOrder, getCalibrationCert,
  getWoComments, addWoComment, getWoPhotoUploadUrl, addWoPhoto, deleteWoPhoto,
  getSummaryReport, getCostReport, getDowntimeReport, getEquipmentOverview,
  uploadToPresignedUrl,
};

export default maintenanceService;
